/**
 * Correcciones de fichaje pendientes.
 *
 * La versión anterior llamaba a /admin/punches/anomalies y
 * /admin/punches/correct — endpoints que NUNCA existieron. Esta va contra la
 * API real: GET /corrections/pending y POST /corrections/{id}/approve.
 *
 * Aprobar no edita nada: el fichaje original se marca CANCELLED y nace uno
 * nuevo encadenado a él. La cabecera lo recuerda porque es lo que hace válido
 * el registro ante Inspección.
 */
import { useCallback, useEffect, useState } from 'react';
import { AdminLayout } from './AdminLayout';
import {
  getPendingCorrections,
  decideCorrection,
  CorrectionListItem,
  apiMessage,
} from '../../services/apiClient';
import { Icon } from '../ui/Icon';

const TYPE_LABEL: Record<string, string> = {
  IN: 'Entrada',
  OUT: 'Salida',
  BREAK_START: 'Inicio de pausa',
  BREAK_END: 'Fin de pausa',
};

export function TimeCorrectionInbox() {
  const [items, setItems] = useState<CorrectionListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<number | null>(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    const res = await getPendingCorrections();
    setItems(res.corrections);
  }, []);

  useEffect(() => {
    load().catch(() => setError('No se pudieron cargar las correcciones'));
  }, [load]);

  // Sin try/catch, un 400 del backend —"esta corrección ya está procesada",
  // el caso real de dos responsables con la bandeja abierta— dejaba la fila
  // tal cual y sin mensaje: el admin pulsaba otra vez y no pasaba nada. La
  // doble escritura en el ledger sí está blindada; el daño era el silencio.
  const approve = async (id: number) => {
    setError(null);
    try {
      await decideCorrection(id, true);
      await load();
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo aprobar la corrección'));
    }
  };

  const reject = async (id: number) => {
    setError(null);
    try {
      await decideCorrection(id, false, reason.trim());
      setRejecting(null);
      setReason('');
      await load();
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo rechazar la corrección'));
    }
  };

  return (
    <AdminLayout title="Correcciones de fichaje">
      <div className="max-w-[920px] space-y-3.5">
        <div className="flex items-center gap-2.5 rounded-xl bg-info-soft border border-info px-4 py-3 text-[14px] text-info">
          <Icon name="info" size={18} strokeWidth={1.8} className="shrink-0" />
          Aprobar no edita el fichaje original. Genera un registro nuevo, encadenado y trazable.
        </div>

        {error && <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14px]">{error}</p>}

        {items.map((c) => {
          const initials = c.user_name
            .split(' ')
            .map((w) => w[0])
            .slice(0, 2)
            .join('');
          return (
            <div key={c.id} className="card-surface p-5 flex gap-4 items-center flex-wrap">
              <span className="w-10 h-10 rounded-lg bg-surface-2 grid place-items-center font-display font-bold text-ink-2 shrink-0">
                {initials}
              </span>
              <div className="flex-1 min-w-[200px]">
                <p className="font-semibold text-[15.5px]">{c.user_name}</p>
                <p className="font-mono text-[13px] text-ink-2 mt-0.5">
                  {TYPE_LABEL[c.punch_type] ?? c.punch_type}{' '}
                  {c.original_time ? (
                    <span className="line-through text-ink-3">{c.original_time.slice(0, 5)}</span>
                  ) : (
                    <span className="text-ink-3">(sin marca)</span>
                  )}
                  {' → '}
                  <span className="text-brand font-semibold">{c.proposed_time.slice(0, 5)}</span>
                  {' · '}
                  {/* r2b20[10] (ronda 4): `new Date('YYYY-MM-DD')` es
                      medianoche UTC — en un navegador con huso negativo el
                      día bajaba uno. La fecha es un DÍA (no un instante): se
                      ancla a medianoche local, el patrón del resto del
                      cuadrante (CalendarGrid). */}
                  {new Date(`${c.correction_date}T00:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                </p>
                <p className="text-[12.5px] text-ink-3 mt-0.5">
                  pedida el {new Date(c.created_at).toLocaleDateString('es-ES')}
                </p>
                {/* r2b20[1]: el motivo del trabajador (obligatorio, min 10
                    chars) viaja en la lista desde el backend: sin él el
                    responsable aprobaba o rechazaba un INSERT retroactivo sin
                    ver la justificación — que es justo lo que luego queda
                    sellado en el ledger como si hubiera sido revisado. */}
                <p className="text-[12.5px] text-ink-3 mt-1">«{c.reason}»</p>
              </div>

              {rejecting === c.id ? (
                <div className="w-full flex flex-wrap gap-2.5 items-end">
                  <label className="block flex-1 min-w-[220px]">
                    <span className="label">Motivo del rechazo (se le muestra al trabajador)</span>
                    <input className="field" value={reason} onChange={(e) => setReason(e.target.value)} />
                  </label>
                  <button onClick={() => setRejecting(null)} className="h-12 px-4 rounded-xl border border-line-strong font-semibold text-[14px]">
                    Cancelar
                  </button>
                  <button
                    onClick={() => reject(c.id)}
                    disabled={reason.trim().length < 5}
                    className="h-12 px-4.5 px-5 rounded-xl bg-danger text-on-danger font-bold text-[14px] disabled:opacity-40"
                  >
                    Confirmar rechazo
                  </button>
                </div>
              ) : (
                <div className="flex gap-2.5 shrink-0">
                  <button
                    onClick={() => setRejecting(c.id)}
                    className="h-10 px-4 rounded-xl border border-line-strong text-danger font-semibold text-[14px]"
                  >
                    Rechazar
                  </button>
                  <button
                    onClick={() => approve(c.id)}
                    className="h-10 px-4.5 px-5 rounded-xl bg-brand text-on-brand font-bold text-[14px]"
                  >
                    Aprobar
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {items.length === 0 && !error && (
          <p className="text-center text-[14px] text-ink-3 py-12">Sin correcciones pendientes ✓</p>
        )}
      </div>
    </AdminLayout>
  );
}
